import {
  createRevealHandover,
  type IRevealHandover,
  REVEAL_HANDOVER_MS
} from "../../../shared/utility/reveal-handover";

/**
 * What the Datasources landing should light up after a save.
 *
 * The editors that create or update a datasource, a table or an action are usually not rendered
 * beside the landing — `data-diagram-canvas` shows one OR the other, so an editor's close unmounts
 * it and mounts the landing fresh. A few savers (the datasource panel's SQL dialogs) sit over a
 * landing that is already on screen. So the hand-over works both ways: a mounted landing listens
 * and takes a request at once; a landing about to mount takes the one left for it.
 *
 * A request left for a landing is only good for a moment — the close that wrote it is about to show
 * the landing. One that finds no landing within `DATASOURCE_REVEAL_HANDOVER_MS` (the save closed
 * onto some other screen) is dropped, so a later visit to Datasources does not light an old row.
 *
 * It names the row rather than carrying its id. A CREATE does not know the id it is about to be
 * given — the save's response shape is untyped and the list is reloaded separately — but it always
 * knows the name it typed, and a name is unique among the rows it could be confused with. The
 * landing resolves the name against whatever rows it currently has, on every render, so a reveal
 * asked for before the refreshed list arrives still lands when it does.
 */
export interface IDatasourceRevealRequest {
  /** The row's name as it appears in the list — a datasource's, a table's or an action's. */
  name: string;
  /**
   * The datasource that has to be open for a CHILD row (a table or an action) to be in the list at
   * all: the tree opens closed, so a new action under a collapsed datasource would never be drawn.
   */
  parentId?: string;
}

/** How long a request waits for a landing to mount before it is dropped. */
export const DATASOURCE_REVEAL_HANDOVER_MS: number = REVEAL_HANDOVER_MS;

const handover: IRevealHandover<IDatasourceRevealRequest> =
  createRevealHandover<IDatasourceRevealRequest>(DATASOURCE_REVEAL_HANDOVER_MS);

/** The saver has succeeded and is about to close. */
export const requestDatasourceReveal: (request: IDatasourceRevealRequest) => void = handover.request;

/** The landing is mounting. Returns the request left for it, once, if it is still fresh. */
export const takeDatasourceReveal: () => IDatasourceRevealRequest | null = handover.take;

/**
 * The landing is on screen: requests reach it directly from now on. Returns the unsubscribe for
 * `componentWillUnmount`.
 */
export const subscribeDatasourceReveal: (onReveal: (request: IDatasourceRevealRequest) => void) => () => void =
  handover.subscribe;
